import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { Alert, Box, Button, Grid, Typography, FormHelperText } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import OZInput from "../../../components/admin/common/OZInput";
import OZSelect from "../../../components/admin/common/OZSelect";
import { Stack } from "@mui/system";
import SimpleSwitch from "../../../components/admin/common/OZSwitch/SimpleSwitch";
import TimePicker from "rc-time-picker";
import OzDatePicker from "../../../components/admin/common/OzDatePicker";
import moment from "moment";
import dayjs from "dayjs";
import "./campaign.css";
import campaignApi from "./campaignApi";
import { getUserId } from "../../../services/auth-service";
import { whatsappApi } from "./whatsappApi";
import { showErrorNotification, showSuccessNotification } from "../../../components/admin/common/NotiService";
import { ScrollToFieldError } from "../../../components/admin/common/ScrollToFieldError";
import { META_LANGUAGES } from "../../../utils/constants";
import { validateWhatsAppVariables } from "./interfaces";
import WhatsAppTemplateVariables from "./WhatsAppTemplateVariables";
import WhatsAppPreview from "./WhatsAppPreview";

// Constants
const INITIAL_CAMPAIGN_DATA = {
    name: '',
    type: 'whatsapp',
    category: 'simple',
    messageType: 'simple',
    sender: '',
    templateName: '',
    templateType: '',
    templateText: '',
    templateLanguage: '',
    allowDuplicate: false,
    numbers: '',
    callbackUrl: '',
    fileUpload: null,
    schedule: {
        scheduled: false,
        startDate: null,
        endDate: null,
        start: "09:00:00",
        end: "20:59:59"
    },
    extraData: {},
    variables: {}
};

const VALIDATION_SCHEMA = yup.object({
    name: yup
        .string()
        .min(2, "Campaign Name should be between 2 to 150 alphanumeric characters long allows special characters like _,space and Campaign Name cannot start and end with Special characters.")
        .max(150, "Campaign Name should be between 2 to 150 alphanumeric characters long allows special characters like _,space and Campaign Name cannot start and end with Special characters.")
        .matches(/^([A-Za-z0-9]((_|\s)*?[A-Za-z0-9])+)$/, 'Campaign Name should be between 2 to 150 alphanumeric characters long allows special characters like _,space and Campaign Name cannot start and end with Special characters.')
        .required("Campaign Name is required").nullable(),
    sender: yup
        .string()
        .required("WhatsApp Number is required").nullable(),
    templateName: yup
        .string()
        .required("Template Name is required").nullable(),
    category: yup
        .string()
        .required("Category is required").nullable(),
    numbers: yup
        .string().nullable()
        .when("category", {
            is: "simple",
            then: yup.string().when("messageType", {
                is: "Simple",
                then: yup.string().required("Phone Numbers are required for simple category").nullable(),
                otherwise: yup.string().notRequired().nullable()
            }),
            otherwise: yup.string().notRequired().nullable()
        }),
    fileUpload: yup
        .mixed().nullable()
        .when("messageType", {
            is: "bulk",
            then: yup.mixed().required("File upload is required for bulk messages").nullable(),
            otherwise: yup.mixed().notRequired().nullable()
        }),
});

// Sub-components
const CampaignHeader = ({ id, campaignName, onClose }) => (
    <Stack spacing={2}>
        <Typography
            component="div"
            variant="h6"
            sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '14px',
                color: '#212121',
                cursor: "pointer",
            }}
            onClick={onClose}
        >
            {`CXI Reach / ${!id ? 'Create' : 'WhatsApp'} Campaign`}
        </Typography>
        <Typography
            component="div"
            variant="h6"
            sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '28px',
                color: '#000000',
            }}
        >
            {`${!id ? 'Create WhatsApp Campaign' : campaignName}`}
        </Typography>
    </Stack>
);

const CampaignStats = ({ campaign }) => (
    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={4}>
            <Alert severity="info" icon={false} action={<Typography sx={{ fontWeight: 'bold' }}>{campaign?.counts.uploaded || 0}</Typography>}>
                Uploaded
            </Alert>
        </Grid>
        <Grid item xs={12} sm={4}>
            <Alert severity="success" icon={false} action={<Typography sx={{ fontWeight: 'bold' }}>{campaign?.counts.sent || 0}</Typography>}>
                Sent
            </Alert>
        </Grid>
        <Grid item xs={12} sm={4}>
            <Alert severity="error" icon={false} action={<Typography sx={{ fontWeight: 'bold' }}>{(campaign?.counts.uploaded || 0) - (campaign?.counts.sent || 0)}</Typography>}>
                Pending
            </Alert>
        </Grid>
    </Grid>
);

const MessageTypeSection = ({ formik, disabled, mode }) => (
    <Grid item xs={6} sm={6}>
        <OZSelect
            name="messageType"
            label="Upload Type *"
            options={["simple", "bulk"]}
            optionLabel="name"
            placeholder="Select Upload Type"
            formik={formik}
            disabled={disabled || mode === 'edit'}
        />
    </Grid>
);

const CategorySection = ({ formik, disabled, mode }) => (
    <>
        <Grid item xs={6} sm={6} md={6}>
            <OZSelect
                name="category"
                label="Template Category *"
                options={["simple", "dynamic"]}
                optionLabel="name"
                placeholder="Select Category"
                formik={formik}
                disabled={disabled || mode === 'edit'}
            />
        </Grid>
        <Grid item xs={6} sm={6} md={6}>
        </Grid>
    </>
);

const FileUploadSection = ({ formik, disabled, onFileChange }) => {
    const onDownload = () => {
        try {
            const link = document.createElement("a");
            link.download = "data_upload_cxi_campaign_whatsapp_sample.xlsx";
            link.href = `${process.env.PUBLIC_URL}/sample_files/data_upload_cxi_campaign_sample.xlsx`;
            link.click();
        } catch (error) {
            showErrorNotification("Something went wrong with file download, kindly refresh the page and try again");
        }
    };

    return (
        <Grid item xs={12} sm={12}>
            {/* Label with download link on the right */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <Typography
                    fontSize='12px'
                    fontWeight={400}
                    lineHeight='14px'
                    marginLeft='1px'
                    color={disabled ? '#99A0A8' : '#212121'}
                >
                    Upload File *
                </Typography>
                <Typography
                    variant="span"
                    sx={{
                        fontSize: '12px',
                        color: '#4B87FF',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                            color: '#3D78E6',
                        }
                    }}
                    onClick={onDownload}
                >
                    Download Sample
                </Typography>
            </div>

            <div style={{ position: 'relative' }}>
                <OZInput
                    name="fileUploadDisplay"
                    placeholder="Choose file..."
                    value={formik.values.fileUpload?.name || ""}
                    disabled={true}
                />
                <Button
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={disabled}
                    sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        textTransform: "none",
                        p: 1,
                        borderRadius: '8px',
                        color: disabled ? '#99A0A8' : '#4B87FF',
                        '&:hover': {
                            backgroundColor: disabled ? 'transparent' : 'rgba(75, 135, 255, 0.04)',
                        },
                    }}
                >
                    Upload
                    <input
                        type="file"
                        hidden
                        onChange={onFileChange}
                        accept=".csv,.xlsx,.xls"
                        disabled={disabled}
                    />
                </Button>
            </div>

            {formik.touched.fileUpload && formik.errors.fileUpload && (
                <FormHelperText error sx={{ marginLeft: '14px', marginTop: '3px' }}>
                    {formik.errors.fileUpload}
                </FormHelperText>
            )}
            <FormHelperText sx={{ marginLeft: '14px', marginTop: '3px', color: '#99A0A8' }}>
                Supported formats: CSV, Excel (.xlsx, .xls) - Max size: 5MB
            </FormHelperText>
        </Grid>
    );
};

const MessageSection = ({ formik, templates, disabled, templateErrors, setTemplateErrors, handleTemplateChange, mode }) => {

    // Check if template is carousel
    const isCarousel = (formik.values?.templateText && formik.values?.templateText?.length > 0)
        ? typeof formik.values.templateText === 'string'
            ? formik.values.templateText.includes('CAROUSEL')
            : JSON.stringify(formik.values.templateText).includes('CAROUSEL')
        : false;

    const selectedTemplate = templates.find((t) => t.name === formik.values.templateName);

    const handleVariableChange = (name, value) => {
        // Update the extraData field in formik
        const newExtraData = {
            ...formik.values.extraData,
            [name]: value
        };
        formik.setFieldValue("extraData", newExtraData);

        // Validate variables when they change
        if (value) {
            const templateTextForValidation =
                typeof formik.values.templateText === "object"
                    ? JSON.stringify(formik.values.templateText)
                    : formik.values.templateText;

            const errors = validateWhatsAppVariables(templateTextForValidation, newExtraData);
            setTemplateErrors(errors);
        }
    };

    const handleFileChange = (file) => {
        // Handle file uploads for WhatsApp templates
        if (file && (file.type.startsWith("image/") || file.type === "application/pdf" || file.type === "video/mp4")) {
            // File will be handled by the parent component
        } else {
            setTemplateErrors({
                ...templateErrors,
                header: "Please upload a valid image, PDF, or video file",
            });
        }
    };

    return <>
        {selectedTemplate?.variables && (formik.values.category === "simple" || formik.values.category === "dynamic" || isCarousel) && (<Grid item xs={6} sm={6}>
            <WhatsAppTemplateVariables
                variables={selectedTemplate.variables}
                onVariableChange={handleVariableChange}
                onFileChange={handleFileChange}
                extraData={formik.values.extraData || {}}
                getFieldError={(field) => formik.errors[field]}
                isSubmitting={mode === 'edit'}
                category={formik.values.category}
            />
        </Grid >
        )}
        <Grid item xs={6} sm={6} >
            <>
                {/* Template Preview */}
                {formik.values.templateName && formik.values.templateText && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%', }}>
                        {/* <Typography variant="h6" gutterBottom>
                                Message Preview
                            </Typography> */}
                        {/* Error display for template validation */}
                        {Object.keys(templateErrors).length > 0 && (formik.values.category !== "dynamic" || isCarousel) && (
                            <Alert
                                severity="error"
                                sx={{
                                    mb: 2,
                                    backgroundColor: "#FEE2E2",
                                    color: "#DC2626",
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    Please fix the following errors:
                                </Typography>
                                {Object.entries(templateErrors).map(([field, error]) => (
                                    <Typography
                                        key={field}
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                    >
                                        • {error}
                                    </Typography>
                                ))}
                            </Alert>
                        )}
                        <WhatsAppPreview
                            templateData={formik.values.templateText}
                            variables={formik.values.extraData || {}}
                            errors={templateErrors}
                        />
                    </Box>
                )}
            </>
        </Grid>
    </>
};

const ScheduleSection = ({ formik, disabled }) => (
    <Grid item xs={12} sm={12}>
        <Box style={{ border: '1px solid #E6E5E6', borderRadius: '8px', padding: '12px' }}>
            <Box style={{ display: 'flex', alignContent: 'center' }}>
                <Typography sx={{
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    color: '#212121',
                }} flexGrow={1}>
                    Schedule *
                </Typography>
                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', fontSize: '12px' }}>
                    <SimpleSwitch
                        name="schedule.scheduled"
                        checked={formik.values.schedule.scheduled}
                        onChange={() => formik.setFieldValue('schedule.scheduled', !formik.values.schedule.scheduled)}
                        disabled={disabled}
                    />
                    <Typography variant="span" sx={{ paddingLeft: 1 }}>Date Schedule</Typography>
                </Box>
            </Box>
            <Box sx={{ borderRadius: '8px', pt: 1 }}>
                <Grid container spacing={2}>
                    {formik.values.schedule.scheduled && (
                        <>
                            <Grid item xs={6} sm={3}>
                                <OzDatePicker
                                    placeholder='Start Date'
                                    name="schedule.startDate"
                                    formik={formik}
                                    label="Start Date"
                                    disableField={disabled}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <OzDatePicker
                                    placeholder='End Date'
                                    name="schedule.endDate"
                                    formik={formik}
                                    label="End Date"
                                    disableField={disabled}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={6} sm={3} >
                        <Typography
                            fontSize='12px'
                            fontWeight={400}
                            lineHeight='14px'
                            marginBottom={'6px'}
                            marginLeft='1px'
                            color="#212121"
                        >
                            Start Time*
                        </Typography>
                        <TimePicker
                            placeholder='Start Time'
                            name="schedule.start"
                            format='HH:mm:ss'
                            label="Start Time"
                            popupStyle={{
                                borderRadius: "8px",
                                overflow: "hidden",
                                zIndex: '9999',
                            }}
                            value={formik.values.schedule.start ? moment(formik.values.schedule.start, 'HH:mm:ss') : null}
                            onChange={(e) => {
                                const isValidDateObject = moment(e).isValid();
                                if (!isValidDateObject) {
                                    showErrorNotification("Invalid Start Time");
                                    return;
                                }
                                formik.setFieldValue('schedule.start', moment(e).format("HH:mm:ss"))
                            }}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Typography
                            fontSize='12px'
                            fontWeight={400}
                            lineHeight='14px'
                            marginBottom={'6px'}
                            marginLeft='1px'
                            color="#212121"
                        >
                            End Time*
                        </Typography>
                        <TimePicker
                            placeholder='End Time'
                            name="schedule.end"
                            format={'HH:mm:ss'}
                            label="End Time"
                            popupStyle={{
                                borderRadius: "8px",
                                overflow: "hidden",
                                zIndex: '9999',
                            }}
                            value={formik.values.schedule.end ? moment(formik.values.schedule.end, 'HH:mm:ss') : null}
                            onChange={(e) => {
                                const isValidDateObject = moment(e).isValid();
                                if (!isValidDateObject) {
                                    showErrorNotification("Invalid End Time");
                                    return;
                                }
                                formik.setFieldValue('schedule.end', moment(e).format("HH:mm:ss"))
                            }}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    </Grid>
);

const PhoneNumbersSection = ({ formik }) => (
    <Grid item xs={12} sm={12}>
        <div>
            <Typography sx={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                color: '#212121',
            }} flexGrow={1}>
                Phone Numbers *
            </Typography>
            <div style={{ marginTop: '2px' }}>
                <OZInput
                    name="numbers"
                    placeholder="Phone Numbers(comma separated) like 9704XXXX,98XXXX210"
                    multiline={true}
                    maxRows={1}
                    height='auto'
                    inputProps={{ style: { height: '60px' } }}
                />
            </div>
        </div>
    </Grid>
);

const ActionButtons = ({ mode, disabled, onCancel, onSubmit, isSubmitting }) => {
    const formButtonStyle = useMemo(
        () => ({
            borderRadius: '8px',
            fontWeight: '400',
            fontSize: '14px',
        }),
        []
    );

    return (
        <Grid container spacing={2} pt={2} alignItems="end" justifyContent="end">
            <Grid item>
                <Button
                    sx={formButtonStyle}
                    type="button"
                    onClick={onCancel}
                    style={formButtonStyle}
                    disabled={isSubmitting}
                    variant="outlined"
                >
                    Cancel
                </Button>
            </Grid>
            <Grid item>
                <Button
                    onClick={onSubmit}
                    disabled={disabled || isSubmitting}
                    style={formButtonStyle}
                    variant="contained"
                >
                    {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}
                </Button>
            </Grid>
        </Grid>
    );
};

// Custom hook for data fetching
const useCampaignData = (userId, campaignId) => {
    const [campaign, setCampaign] = useState(INITIAL_CAMPAIGN_DATA);
    const [whatsappNumbers, setWhatsappNumbers] = useState([]);
    const [whatsappTemplates, setWhatsappTemplates] = useState([]);
    const [loadingNumbers, setLoadingNumbers] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (campaignId) {
                    const campaignResponse = await campaignApi.getCampaign(userId, campaignId);
                    let data = campaignResponse?.data;
                    if (data && typeof data === "object") {
                        if (data.schedule) {
                            if (data.schedule.startDate) data.schedule.startDate = dayjs(data.schedule.startDate);
                            if (data.schedule.endDate) data.schedule.endDate = dayjs(data.schedule.endDate);
                            console.log('WhatsApp campaign data', data.schedule)
                        }
                    }
                    setCampaign(data);
                }

                // Fetch WhatsApp numbers
                setLoadingNumbers(true);
                const numbersResponse = await whatsappApi.listWhatsAppNumbers(userId);
                if (numbersResponse.data?.content) {
                    setWhatsappNumbers(numbersResponse.data.content);
                }

                // Fetch WhatsApp templates immediately when form opens
                setLoadingTemplates(true);
                const templatesResponse = await whatsappApi.getWhatsAppTemplates(userId);
                if (templatesResponse.data?.content) {
                    let templatesList = templatesResponse.data.content;
                    setWhatsappTemplates(templatesList);
                }
            } catch (error) {
                console.error('Error fetching campaign data:', error);
            } finally {
                setLoadingNumbers(false);
                setLoadingTemplates(false);
            }
        };

        fetchData();
    }, [userId, campaignId]);

    return { campaign, whatsappNumbers, whatsappTemplates, loadingNumbers, loadingTemplates };
};

// Utility functions
const convertToListFormat = (data, label) => {
    try {
        if (data && Array.isArray(data)) {
            return data.map(item => item[label]);
        }
        return [];
    } catch (error) {
        console.error('Error converting to list format:', error);
        return [];
    }
};

// Main component
const CxiWhatsAppCampaignForm = ({ id, onClose, refreshData }) => {
    const mode = id ? 'edit' : 'create';
    const [userId] = useState(getUserId() || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [templateErrors, setTemplateErrors] = useState({});
    const { campaign, whatsappNumbers, whatsappTemplates, loadingNumbers, loadingTemplates } = useCampaignData(userId, id);

    const disabled = ['completed', 'running'].includes(campaign?.position || '');

    const handleTemplateChange = (templateName, formik) => {
        if (templateName && whatsappTemplates.length > 0) {
            const selectedTemplate = whatsappTemplates.find((t) => t.name === templateName);
            if (selectedTemplate) {
                // Update template name
                // formik.setFieldValue("templateName", templateName);
                // Update template-related fields
                formik.setFieldValue("templateType", selectedTemplate.type);
                formik.setFieldValue("templateText", selectedTemplate.text);
                formik.setFieldValue("templateLanguage", selectedTemplate.language);
                formik.setFieldValue("templateSubType", selectedTemplate?.subType || '');

                // Clear any existing template errors when selecting a new template
                setTemplateErrors({});
            }
        }
    };

    const handleFileChange = (event, formik) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file size (5MB limit)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                showErrorNotification("File size must be less than 5MB");
                return;
            }

            // Validate file type
            const allowedTypes = ['.csv', '.xlsx', '.xls'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedTypes.includes(fileExtension)) {
                showErrorNotification("Please upload a valid CSV or Excel file");
                return;
            }

            formik.setFieldValue("fileUpload", file);
        }
    };

    const handleSubmit = async (values) => {
        console.log("🚀 ~ handleSubmit ~ WhatsApp values:", values);
        let formData = { ...values };

        if (formData?.schedule?.startDate === "Invalid date") formData.schedule.startDate = null;
        if (formData?.schedule?.endDate === "Invalid date") formData.schedule.endDate = null;

        if (formData && typeof formData === 'object' && "schedule" in formData && typeof formData.schedule === 'object') {
            if ('startDate' in formData.schedule) {
                // Handle dayjs object from OzDatePicker
                let isValidDate = false;
                let formattedStartDate = null;

                if (formData.schedule.startDate) {
                    // Check if it's a dayjs object
                    if (dayjs.isDayjs(formData.schedule.startDate)) {
                        isValidDate = formData.schedule.startDate.isValid();
                        formattedStartDate = isValidDate ? formData.schedule.startDate.format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ startDate is dayjs object, formatted:", formattedStartDate);
                    } else {
                        // Fallback to moment for other date formats
                        isValidDate = moment(formData.schedule.startDate).isValid();
                        formattedStartDate = isValidDate ? moment(formData.schedule.startDate).format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ startDate is not dayjs, formatted with moment:", formattedStartDate);
                    }
                }

                if (!isValidDate && "scheduled" in formData.schedule && formData.schedule.scheduled) {
                    showErrorNotification("Invalid Start Date");
                    return;
                }
                formData.schedule.startDate = formattedStartDate;
            }
            if ('endDate' in formData.schedule) {
                // Handle dayjs object from OzDatePicker
                let isValidDate = false;
                let formattedEndDate = null;

                if (formData.schedule.endDate) {
                    // Check if it's a dayjs object
                    if (dayjs.isDayjs(formData.schedule.endDate)) {
                        isValidDate = formData.schedule.endDate.isValid();
                        formattedEndDate = isValidDate ? formData.schedule.endDate.format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ endDate is dayjs object, formatted:", formattedEndDate);
                    } else {
                        // Fallback to moment for other date formats
                        isValidDate = moment(formData.schedule.endDate).isValid();
                        formattedEndDate = isValidDate ? moment(formData.schedule.endDate).format("YYYY-MM-DD") : null;
                        console.log("🚀 ~ endDate is not dayjs, formatted with moment:", formattedEndDate);
                    }
                }

                if (!isValidDate && "scheduled" in formData.schedule && formData.schedule.scheduled) {
                    showErrorNotification("Invalid End Date");
                    return;
                }
                formData.schedule.endDate = formattedEndDate;
            }
            const start = moment(formData.schedule.start, 'HH:mm:ss');
            const end = moment(formData.schedule.end, 'HH:mm:ss');
            if (end.isBefore(start)) {
                showErrorNotification("End time must be after start time");
                return;
            }
        }

        const isCarousel = typeof formData.templateText === 'string'
            ? formData?.templateText?.includes('CAROUSEL')
            : JSON.stringify(formData?.templateText)?.includes('CAROUSEL');

        if (formData.templateText && mode === 'create') {
            if (formData.category === "simple" || isCarousel) {
                let templateTextForValidation = formData.templateText;
                if (typeof formData.templateText === "object") {
                    templateTextForValidation = JSON.stringify(formData.templateText);
                }

                const errors = validateWhatsAppVariables(templateTextForValidation, formData.extraData);
                console.log("🚀 ~ handleSubmit ~ errors: validateWhatsAppVariables", errors);
            }
        }

        const formDataToAdd = new FormData();
        const extraData = { ...formData.extraData };

        // Handle WhatsApp template variables and file if category is simple
        if (mode === 'create' && (formData.category === 'simple' || isCarousel || formData.category === 'dynamic')) {
            // Process header variables - only one will be present at a time
            const headerTypes = ['header_image', 'header_video', 'header_document'];
            for (const type of headerTypes) {
                if (formData.extraData[type]) {
                    if (formData.extraData[type] instanceof File) {
                        const file = formData.extraData[type];
                        formDataToAdd.append('whatsAppFile', file, file.name);
                    }
                    extraData[type] = ''; // Set empty string for header variable
                    break; // Exit loop since only one header will be present
                }
            }

            // Process body variables
            Object.entries(formData.extraData).forEach(([key, value]) => {
                if (key.startsWith('body_')) {
                    extraData[key] = value;
                }
            });
        }

        formData.extraData = extraData;

        if (mode === 'create' && formData.messageType === 'bulk') {
            if (formData.fileUpload) {
                formDataToAdd.append('file', formData.fileUpload);
            }
            delete formData.numbers;
        }

        delete formData.messageType;
        delete formData.fileUpload;

        if (formData.type === "whatsapp" && mode === 'create') {
            let whatsAppFiles = [];
            try {
                const templateJson = JSON.parse(formData.templateText);
                if (templateJson.map((item) => item.type).includes("CAROUSEL")) {
                    const carouselExtraDataVariables = formData.extraData;
                    console.log("carouselExtraDataVariables", structuredClone(carouselExtraDataVariables));
                    for (const key in carouselExtraDataVariables) {
                        if (key.startsWith('carousel_') && (key.endsWith('_image') || key.endsWith('_video'))) {
                            const file = formData.extraData[key];
                            const fileName = file.name;
                            console.log("fileName", fileName);
                            console.log("file", file);
                            whatsAppFiles.push(file);
                            formData.extraData[key] = fileName;
                        } else {
                            formData.extraData[key] = carouselExtraDataVariables[key];
                        }
                    }
                }
            } catch (error) {
                console.error('Error parsing template text:', error);
            }
            console.log("whatsAppFiles", whatsAppFiles);
            if (whatsAppFiles.length > 0) {
                for (const file of whatsAppFiles) {
                    formDataToAdd.append('whatsAppFiles', file, file.name);
                }
            }
        }

        formDataToAdd.append('campaign', new Blob([JSON.stringify(formData)], {
            type: 'application/json'
        }));

        console.log("formData", formData);
        console.log('FormData entries:', [...formDataToAdd.entries()]);

        setIsSubmitting(true);
        try {
            let response;
            if (mode === 'edit') {
                response = await campaignApi.updateCampaign(userId, formData.id, formData);
            } else {
                response = await campaignApi.createCampaign(userId, formData, formDataToAdd);
            }
            if (response && typeof response === "object" && response.status === "success") {
                showSuccessNotification(response?.message === "success" ? `Campaign ${mode === 'edit' ? 'updated' : 'created'} successfully` : response?.message);
                if (refreshData) {
                    refreshData();
                }
                if (onClose) {
                    onClose();
                }
            } else {
                showErrorNotification(response?.message || "Failed to create campaign");
            }
        } catch (error) {
            console.error('Error saving campaign:', error);
            let firstErr = '';
            if (error.response?.data?.errors && typeof error.response?.data?.errors === 'object' && Object.keys(error.response?.data?.errors).length > 0) {
                let errors = error.response?.data?.errors;
                for (let err in errors) {
                    if (errors[err] && Array.isArray(errors[err]) && errors[err]?.length > 0) {
                        firstErr = errors[err][0];
                        break;
                    }
                }
            }
            showErrorNotification(
                firstErr ||
                error.response?.data?.message ||
                `Failed to ${mode === 'edit' ? 'update' : 'create'} campaign`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    return (
        <div className="cxi-whatsapp-campaign-form">
            <CampaignHeader id={id} campaignName={campaign?.name} onClose={onClose} />

            <Formik
                initialValues={campaign}
                validationSchema={VALIDATION_SCHEMA}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <Stack spacing={2} sx={{ padding: 2, my: 1.5, backgroundColor: '#fff', width: '65vw', borderRadius: '8px' }}>

                        {disabled && (
                            <Alert severity="info">
                                Campaign Edit is disabled for completed or running campaigns.
                            </Alert>
                        )}

                        <form id="whatsAppCampaignForm" onSubmit={formik.handleSubmit}>
                            {formik.values?.id && mode === 'edit' && <CampaignStats campaign={campaign} />}

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <OZInput
                                        name="name"
                                        placeholder="Enter Campaign Name"
                                        label="Campaign Name *"
                                        disabled={disabled || mode === 'edit'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <OZInput
                                        name="callbackUrl"
                                        placeholder="Callback URL"
                                        label="Callback URL"
                                        disabled={disabled}
                                        fieldType="url"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <OZSelect
                                        name="sender"
                                        label="WhatsApp Number *"
                                        options={convertToListFormat(whatsappNumbers, "number")}
                                        optionLabel="number"
                                        placeholder="Select WhatsApp Number"
                                        formik={formik}
                                        disabled={disabled || mode === 'edit'}
                                        loading={loadingNumbers}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <OZSelect
                                        name="templateName"
                                        label="Template Name *"
                                        options={convertToListFormat(whatsappTemplates, "name")}
                                        optionLabel="name"
                                        placeholder="Select Template Name"
                                        formik={formik}
                                        disabled={disabled || mode === 'edit'}
                                        loading={loadingTemplates}
                                        OnSaveCallBack={(name, option, value) => {
                                            // Handle the template selection and auto-populate fields
                                            handleTemplateChange(value, formik);
                                        }}
                                    />
                                    {/* Show template info below selection */}
                                    {formik.values.templateName && (
                                        <Typography variant="caption" color="textSecondary" sx={{ mt: '2px', display: 'block', fontSize: '11px' }}>
                                            {(() => {
                                                const selectedTemplate = whatsappTemplates.find(t => t.name === formik.values.templateName);
                                                if (selectedTemplate) {
                                                    const languageLabel = META_LANGUAGES.find((lang) => lang.value === selectedTemplate.language)?.label || selectedTemplate.language;
                                                    const isCallingConsent = selectedTemplate?.subType && typeof selectedTemplate?.subType === "string" && selectedTemplate?.subType?.length > 0 && selectedTemplate.subType.includes("CALL_PERMISSIONS_REQUEST");
                                                    return <span style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                                                        <span>Language: {languageLabel},</span>
                                                        <span>Type: {selectedTemplate.type},</span>
                                                        <span>Status: <span style={{ color: selectedTemplate.approved ? "green" : "red", fontWeight: 'bold' }}>{selectedTemplate.approved ? "Approved" : "Not Approved"},</span></span>
                                                        <span>Deleted: <span style={{ color: !selectedTemplate.deleted ? "green" : "red", fontWeight: 'bold' }}>{selectedTemplate.deleted ? "Yes" : "No"}</span>,</span>
                                                        <span>Calling Consent: <span style={{ color: isCallingConsent ? "green" : "red", fontWeight: 'bold' }}>{isCallingConsent ? "Yes" : "No"}</span></span>
                                                    </span>
                                                }
                                                return '';
                                            })()}
                                        </Typography>
                                    )}
                                </Grid>

                                {/* Category Section - only for new campaigns otherwise disabled */}
                                <CategorySection formik={formik} disabled={disabled} mode={mode} />

                                <MessageSection
                                    formik={formik}
                                    templates={whatsappTemplates}
                                    disabled={disabled}
                                    templateErrors={templateErrors}
                                    setTemplateErrors={setTemplateErrors}
                                    handleTemplateChange={handleTemplateChange}
                                    mode={mode}
                                />

                                <ScheduleSection formik={formik} disabled={disabled} />
                                {/* Message Type Section - only for new campaigns */}
                                {!formik.values?.id && mode === 'create' && (
                                    <MessageTypeSection formik={formik} disabled={disabled} mode={mode} />
                                )}
                                {/* File Upload Section - only for new campaigns with Bulk type */}
                                {!formik.values?.id && formik.values.messageType === 'bulk' && mode === 'create' && (
                                    <FileUploadSection formik={formik} disabled={disabled} onFileChange={(e) => handleFileChange(e, formik)} />
                                )}
                                {/* Phone Numbers Section - only for new campaigns with Simple type */}
                                {!formik.values?.id && formik.values.messageType === 'simple' && mode === 'create' && (
                                    <PhoneNumbersSection formik={formik} />
                                )}
                                <Grid item xs={6} sm={6}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', fontSize: '12px' }}>
                                        <SimpleSwitch
                                            name="allowDuplicate"
                                            checked={formik.values.allowDuplicate}
                                            onChange={() => formik.setFieldValue("allowDuplicate", !formik.values.allowDuplicate)}
                                            disabled={disabled || mode === 'edit'}
                                        />
                                        <Typography variant="span" sx={{ paddingLeft: 1 }}>Allow Duplicate</Typography>
                                    </div>
                                </Grid>
                            </Grid>
                            <ActionButtons
                                mode={mode}
                                disabled={disabled}
                                onCancel={handleCancel}
                                onSubmit={() => {
                                    if (formik.isValid) {
                                        formik.handleSubmit()
                                    } else {
                                        if (formik.errors) {
                                            for (let err in formik.errors) {
                                                if (err && typeof err === 'string' && formik.errors[err] && typeof formik.errors[err] === 'string') {
                                                    showErrorNotification(formik.errors[err]);
                                                    break;
                                                }
                                            }
                                        }
                                        formik.validateForm()
                                    }
                                }}
                                isSubmitting={isSubmitting}
                            />
                            <ScrollToFieldError />
                        </form>
                    </Stack>
                )}
            </Formik>
        </div>
    );
};

export default CxiWhatsAppCampaignForm;